import { Link } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';

const BASE_URL = 'https://momentsent.com';
const PATH = '/what-to-write-in-an-anniversary-card';

/**
 * Message-ideas guide: /what-to-write-in-an-anniversary-card
 *
 * A genuinely useful, citable resource (GEO play): answer-first intro, then
 * dozens of ready-to-use anniversary messages grouped by relationship and tone.
 * Cross-links to /anniversary-cards and /signup. Emits Article + FAQPage +
 * BreadcrumbList JSON-LD.
 */

interface MessageGroup {
  heading: string;
  intro:   string;
  messages: string[];
}

const GROUPS: MessageGroup[] = [
  {
    heading: 'Romantic, for a partner or spouse',
    intro:   'Make it personal and a little romantic. This is the card they will keep.',
    messages: [
      'Happy anniversary to the love of my life. Every year with you is my favorite year.',
      'I would choose you again, in every lifetime and every version of this one. Happy anniversary.',
      'Thank you for a love that still feels new. Here is to us, today and always. Happy anniversary.',
      'Of all the things I have done right, loving you is the best. Happy anniversary, my love.',
      'Years later and you are still the best part of every day. Happy anniversary.',
      'Here is to another year of you and me. I love you more now than ever. Happy anniversary.',
    ],
  },
  {
    heading: 'Short and sweet',
    intro:   'Sometimes a few warm words say it all.',
    messages: [
      'Happy anniversary! Here is to us.',
      'Another year, still in love. Happy anniversary.',
      'So grateful for you and for us. Happy anniversary.',
      'Cheers to another year together. I love you.',
      'Happy anniversary to my favorite person.',
      'Still my favorite hello and my hardest goodbye. Happy anniversary.',
    ],
  },
  {
    heading: 'Funny and lighthearted',
    intro:   'A little humor keeps a long love light. Keep it kind.',
    messages: [
      'Happy anniversary! Thanks for putting up with me for another whole year. That is true love.',
      'We have made it another year without driving each other completely crazy. Cheers to us!',
      'Happy anniversary to the person who still laughs at my jokes, or at least pretends to.',
      'They say marriage is a work in progress. Good thing we are both hard workers. Happy anniversary!',
      'Still my favorite weirdo after all these years. Happy anniversary.',
    ],
  },
  {
    heading: 'For a couple (friends or family)',
    intro:   'Celebrate someone else’s anniversary with a warm word for the two of them.',
    messages: [
      'Happy anniversary to two people who show us all what real love looks like.',
      'Wishing you both another year as wonderful as the love you share. Happy anniversary.',
      'Congratulations on another year together. You two are the real deal. Happy anniversary!',
      'Here is to a couple who makes it look easy. Wishing you many more happy years.',
      'Happy anniversary! May your love keep growing for many years to come.',
    ],
  },
  {
    heading: 'Milestone anniversary',
    intro:   'Big anniversaries like the 10th, 25th, and 50th deserve a message that honors the journey.',
    messages: [
      'Twenty five years of love and counting. What a beautiful thing to celebrate. Happy anniversary.',
      'Fifty years together is a love story for the ages. Congratulations and happy anniversary.',
      'A milestone like this is proof of a love built to last. Wishing you many more years. Happy anniversary.',
      'Look how far you have come, and how much love you have built. Happy milestone anniversary.',
      'Here is to a lifetime of love and the many wonderful years still ahead. Happy anniversary.',
    ],
  },
  {
    heading: 'For your parents',
    intro:   'A parents’ anniversary is a chance to thank them for the example they set.',
    messages: [
      'Happy anniversary to the two people who taught me what love looks like.',
      'Thank you for showing us all how it is done. Happy anniversary, Mom and Dad.',
      'Your love has always been the heart of this family. Happy anniversary.',
      'Grateful for the home and the love you built together. Happy anniversary.',
      'Wishing you both another year as wonderful as you are. Happy anniversary, with love.',
    ],
  },
];

const FAQ = [
  {
    q: 'What should I write in an anniversary card?',
    a: 'Start with "Happy anniversary," add one personal line about your relationship or a shared memory, and close with a wish for the years ahead and your name. Keep it genuine: a short, sincere note means more than a long, generic one. If you are stuck, pick a message from this guide that matches your relationship and tone and change a word or two to make it yours.',
  },
  {
    q: 'What is a good short anniversary message?',
    a: 'A good short anniversary message is warm and personal, for example: "Another year, still in love. Happy anniversary." or "So grateful for you and for us. Happy anniversary." Short messages work well when you want something simple and heartfelt.',
  },
  {
    q: 'What do you write for a milestone anniversary like the 25th or 50th?',
    a: 'Acknowledge the journey and the years shared, for example: "Twenty five years of love and counting. What a beautiful thing to celebrate." or "Fifty years together is a love story for the ages. Congratulations." Milestone messages work best when they honor how far the couple has come.',
  },
  {
    q: 'How do I sign off an anniversary card?',
    a: 'For a partner, "All my love" or "Forever yours" fits well. For a couple you are celebrating, "With love" or "Congratulations" works, then your name. Keep the closer warm and match it to your relationship.',
  },
];

export default function WhatToWriteAnniversaryPage() {
  const canonical = `${BASE_URL}${PATH}`;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'What to Write in an Anniversary Card: 30+ Message Ideas',
      description: 'Ready-to-use anniversary card messages grouped by relationship and tone, from romantic to funny, plus milestone wishes and tips on what to write and how to sign off.',
      author: { '@type': 'Organization', name: 'MomentSent' },
      publisher: {
        '@type': 'Organization',
        name: 'MomentSent',
        logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo-wordmark.png` },
      },
      mainEntityOfPage: canonical,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'What to Write in an Anniversary Card', item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <PageSEO
        title="What to Write in an Anniversary Card: 30+ Message Ideas | MomentSent"
        description="Stuck on what to write in an anniversary card? Use these ready-to-go messages grouped by relationship and tone, from romantic to funny, plus milestone wishes and tips on how to sign off."
        canonical={canonical}
        structuredData={structuredData}
      />
      <LandingNav />

      {/* ── Hero / answer-first intro ───────────────────────────────────────── */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-sm text-sage font-semibold tracking-widest uppercase mb-4">
            Message ideas
          </p>
          <h1 className="font-heading text-4xl md:text-5xl text-navy leading-tight mb-6">
            What to write in an anniversary card
          </h1>
          <p className="font-body text-lg text-navy/80 leading-relaxed">
            The best anniversary card messages are warm, personal, and a little romantic. Start with
            "Happy anniversary," add one specific line about your relationship or a shared memory, and
            close with a wish for the years ahead. Below are more than 30 ready-to-use messages grouped
            by relationship and tone. Pick one that fits, change a word or two to make it yours, and you
            are done.
          </p>
        </div>
      </section>

      {/* ── Message groups ──────────────────────────────────────────────────── */}
      <section className="pb-4 px-6">
        <div className="max-w-3xl mx-auto space-y-10">
          {GROUPS.map((g) => (
            <div key={g.heading}>
              <h2 className="font-heading text-2xl text-navy mb-2">{g.heading}</h2>
              <p className="font-body text-base text-gray-500 leading-relaxed mb-5">{g.intro}</p>
              <ul className="space-y-3">
                {g.messages.map((m) => (
                  <li
                    key={m}
                    className="bg-white rounded-card p-5 shadow-warm border border-gray-100
                               font-body text-base text-gray-700 leading-relaxed"
                  >
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── Tips on writing the card ────────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">Tips for writing an anniversary card</h2>
          <ul className="space-y-4">
            {[
              'Open with "Happy anniversary." It sets the tone and works for any relationship.',
              'Add one specific detail. A shared memory, a quality you love, or a hope for the year ahead makes the card personal.',
              'Match the tone to the relationship. Romantic for a partner, warm for a couple you are celebrating, sincere for parents.',
              'For milestones, name the years. Acknowledging the journey makes a big anniversary feel honored.',
              'Sign off with the right closer. "All my love" or "Forever yours" for a partner, "With love" or "Congratulations" for others, then your name.',
            ].map((t) => (
              <li key={t} className="flex gap-3">
                <span className="text-sage font-bold mt-0.5" aria-hidden="true">✓</span>
                <span className="font-body text-base text-gray-600 leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">Frequently asked questions</h2>
          <div className="space-y-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="font-heading text-lg text-navy mb-2">{f.q}</h3>
                <p className="font-body text-base text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA back to the anniversary product ─────────────────────────────── */}
      <section className="py-20 px-6 bg-sage/10 mt-auto">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-navy mb-4">
            Found the perfect words? Let us mail the card.
          </h2>
          <p className="font-body text-base text-gray-500 leading-relaxed mb-8">
            With <Link to="/anniversary-cards" className="text-sage font-semibold hover:underline">automatic anniversary cards</Link> from
            MomentSent, you add the date once and we mail a real, physical card every year, right on
            time, with your message inside.
          </p>
          <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
            Start your free trial →
          </Link>
          <p className="mt-3 font-body text-xs text-gray-400">
            14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
